import "@/App.css";
import "@/design-system.css";
import "@/screens.css";
import "@/homepage.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { BusinessAuthProvider } from "@/context/BusinessAuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/context/ThemeContext";
import BottomTabBar from "@/components/BottomTabBar";
import { analytics } from "@/utils/analytics";

// Guest Pages
import HomePage from "@/pages/guest/HomePage";
import CategoryPage from "@/pages/guest/CategoryPage";
import EatCategoryPage from "@/pages/guest/EatCategoryPage";
import BusinessDetailPage from "@/pages/guest/BusinessDetailPage";
import SavedPage from "@/pages/guest/SavedPage";
import MapPage from "@/pages/guest/MapPage";
import InfoPage from "@/pages/guest/InfoPage";
import AnalyticsDemoPage from "@/pages/guest/AnalyticsDemoPage";

// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBusinessList from "@/pages/admin/AdminBusinessList";
import AdminBusinessForm from "@/pages/admin/AdminBusinessForm";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";

// Partner Pages
import PartnerLandingPage from "@/pages/partner/PartnerLandingPage";
import PartnerLoginPage from "@/pages/partner/PartnerLoginPage";
import PartnerVerifyPage from "@/pages/partner/PartnerVerifyPage";

// Portal Pages
import OnboardingPage from "@/pages/portal/OnboardingPage";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalListingPage from "@/pages/portal/PortalListingPage";
import PortalMediaPage from "@/pages/portal/PortalMediaPage";
import PortalBillingPage from "@/pages/portal/PortalBillingPage";
import PortalAnalyticsPage from "@/pages/portal/PortalAnalyticsPage";

// Auth Components
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import BusinessProtectedRoute from "@/components/BusinessProtectedRoute";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check if on admin, partner, or portal route (no bottom tab bar)
  const hideTabBar = 
    location.pathname.startsWith('/admin') || 
    location.pathname.startsWith('/partner') || 
    location.pathname.startsWith('/portal');

  return (
    <div className={hideTabBar ? "dq-screens dq-workspace" : "dq-screens dq-guest-screens"}>
      <Routes>
        {/* Guest Routes - No auth required */}
        <Route path="/" element={<HomePage />} />
        <Route path="/category/eat" element={<EatCategoryPage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/business/:id" element={<BusinessDetailPage />} />
        <Route path="/saved" element={<SavedPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/info" element={<InfoPage />} />
        <Route path="/analytics-demo" element={<AnalyticsDemoPage />} />
        
        {/* Partner Routes - Business signup */}
        <Route path="/partner" element={<PartnerLandingPage />} />
        <Route path="/partner/login" element={<PartnerLoginPage />} />
        <Route path="/partner/verify" element={<PartnerVerifyPage />} />
        
        {/* Portal Routes - Business dashboard */}
        <Route path="/portal" element={
          <BusinessProtectedRoute>
            <PortalDashboard />
          </BusinessProtectedRoute>
        } />
        <Route path="/portal/onboarding" element={
          <BusinessProtectedRoute>
            <OnboardingPage />
          </BusinessProtectedRoute>
        } />
        <Route path="/portal/listing" element={
          <BusinessProtectedRoute>
            <PortalListingPage />
          </BusinessProtectedRoute>
        } />
        <Route path="/portal/media" element={
          <BusinessProtectedRoute>
            <PortalMediaPage />
          </BusinessProtectedRoute>
        } />
        <Route path="/portal/billing" element={
          <BusinessProtectedRoute>
            <PortalBillingPage />
          </BusinessProtectedRoute>
        } />
        <Route path="/portal/analytics" element={
          <BusinessProtectedRoute>
            <PortalAnalyticsPage />
          </BusinessProtectedRoute>
        } />
        
        {/* Admin Auth */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        
        {/* Admin Protected Routes */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/businesses" element={
          <ProtectedRoute>
            <AdminBusinessList />
          </ProtectedRoute>
        } />
        <Route path="/admin/businesses/new" element={
          <ProtectedRoute>
            <AdminBusinessForm />
          </ProtectedRoute>
        } />
        <Route path="/admin/businesses/:id/edit" element={
          <ProtectedRoute>
            <AdminBusinessForm />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute>
            <AdminAnalyticsPage />
          </ProtectedRoute>
        } />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Bottom Tab Bar - only for guest routes */}
      {!hideTabBar && <BottomTabBar />}
    </div>
  );
}

function App() {
  // Initialize analytics tracking on app load
  useEffect(() => {
    analytics.init();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <FavoritesProvider>
          <BusinessAuthProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </BusinessAuthProvider>
        </FavoritesProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
